import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Control,
  FieldValues,
  Path,
  useController
} from 'react-hook-form';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import clsx from 'clsx';
import useAuthContext from 'src/hooks/useAuthContext';
import DAILabel from 'src/DAIComponents/Common/DAILabel';

dayjs.extend(utc);
dayjs.extend(timezone);

type DatePickerProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  required?: boolean
  flexDirection?: 'row' | 'col'

};

export default function DAIDateInput<T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  flexDirection = 'col',
}: DatePickerProps<T>) {
  const { user } = useAuthContext();
  const timezone = user?.timezone;
  const { field, fieldState } = useController<T>({ name, control });

  const today = dayjs().tz(timezone);
  const [selected, setSelected] = useState<Dayjs | null>(field.value ? dayjs(field.value).tz(timezone) : null);
  const [viewingMonth, setViewingMonth] = useState(today.month());
  const [viewingYear, setViewingYear] = useState(today.year());
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const formattedDisplay = selected ? selected.format("YYYY-MM-DD") : "";

  const years = Array.from({ length: 50 }, (_, i) => today.year() - 25 + i);
  const months = Array.from({ length: 12 }, (_, i) => i);

  const daysInMonth = useMemo(
    () => dayjs().year(viewingYear).month(viewingMonth).daysInMonth(),
    [viewingMonth, viewingYear]
  );
  const startOfMonth = dayjs()
    .year(viewingYear)
    .month(viewingMonth)
    .startOf('month')
    .day();

  const weeks: (Dayjs | null)[][] = [];
  let day = 1 - startOfMonth;
  while (day <= daysInMonth) {
    const week: (Dayjs | null)[] = [];
    for (let i = 0; i < 7; i++) {
      const d = dayjs().year(viewingYear).month(viewingMonth).date(day).tz(timezone);
      week.push(day > 0 && day <= daysInMonth ? d : null);
      day++;
    }
    weeks.push(week);
  }

  const handleSelectDate = (date: Dayjs) => {
    const midnightDate = date.startOf('day').tz(timezone, false);
    setSelected(midnightDate);
    field.onChange(midnightDate.toISOString());
    setOpen(false);
  };

  useEffect(() => {
    if (field.value) {
      const date = dayjs(field.value).tz(timezone);
      setViewingMonth(date.month());
      setViewingYear(date.year());
      setSelected(date);
    }
  }, [field.value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative w-full" ref={containerRef}>
      {label && <DAILabel name={label} required={required} />}
      <input
        readOnly
        onClick={() => setOpen(!open)}
        value={formattedDisplay}
        className={`w-full px-3 py-2 ${flexDirection == "col" && "mt-1.5"} text-sm cursor-pointer ring-gray-300 rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset  placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600`}
        placeholder="Select date"
      />

      {open && (
        <div className="absolute z-50 mt-2 p-3 border rounded bg-white shadow-lg">
          <div className="flex items-center gap-2 mb-2">
            <select
              value={viewingMonth}
              onChange={(e) => setViewingMonth(Number(e.target.value))}
              className="border rounded px-2 py-1 text-sm min-w-28"
            >
              {months.map((m) => (
                <option key={m} value={m}>
                  {dayjs().month(m).format('MMMM')}
                </option>
              ))}
            </select>
            <select
              value={viewingYear}
              onChange={(e) => setViewingYear(Number(e.target.value))}
              className="border rounded px-2 py-1 text-sm min-w-20"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 mb-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d}>{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {weeks.map((week, i) =>
              week.map((d, j) => (
                <button
                  key={`${i}-${j}`}
                  onClick={() => d && handleSelectDate(d)}
                  disabled={!d}
                  className={clsx(
                    "p-2 rounded text-sm",
                    d?.isSame(selected, 'day') ? 'bg-blue-500 text-white' : 'hover:bg-gray-100',
                    !d && 'cursor-default'
                  )}
                >
                  {d ? d.date() : ''}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {fieldState.error && (
        <p className="text-sm text-red-500 mt-1">{fieldState.error.message}</p>
      )}
    </div>
  );
}
